"use client";

import Image from "next/image";
import Link from "next/link";
import { useEffect, useState } from "react";
import { NavBar } from "@/components/ui/tubelight-navbar";
import { cn } from "@/lib/utils";
import { navigation, type NavKey } from "./site-content";

export function SiteHeader({
  active,
  heroTransparent = false,
}: {
  active: NavKey;
  heroTransparent?: boolean;
}) {
  const [isScrolled, setIsScrolled] = useState(false);
  const activeItem = navigation.find((item) => item.key === active);
  const navItems = navigation.map((item) => ({
    name: item.label,
    url: item.href,
    icon: item.key,
  }));
  const isHeroTop = heroTransparent && !isScrolled;

  useEffect(() => {
    if (!heroTransparent) return;

    const handleScroll = () => {
      setIsScrolled(window.scrollY > 8);
    };

    handleScroll();
    window.addEventListener("scroll", handleScroll, { passive: true });

    return () => window.removeEventListener("scroll", handleScroll);
  }, [heroTransparent]);

  return (
    <header
      className={cn(
        heroTransparent ? "fixed inset-x-0 top-0" : "sticky top-0",
        "z-50 transition-[background-color,border-color,box-shadow,backdrop-filter] duration-300",
        isHeroTop
          ? "border-b border-transparent bg-transparent shadow-none backdrop-blur-0"
          : "border-b border-[#050505]/10 bg-[#fffdf2]/92 shadow-[0_10px_32px_rgba(140,0,0,0.08)] backdrop-blur",
      )}
      data-hero-state={isHeroTop ? "top" : "scrolled"}
    >
      <div className="mx-auto flex min-h-14 w-full max-w-[1320px] items-center justify-between gap-2 px-3 py-2 md:min-h-16 md:gap-3 md:px-6">
        <Link href="/" className="flex shrink-0 items-center gap-2.5" aria-label="MOTIONA 재활운동센터 홈">
          <Image
            src="/images/logo.png"
            alt="MOTIONA 재활운동센터"
            width={188}
            height={64}
            className={cn(
              "h-8 w-auto transition duration-300 sm:h-9 md:h-12",
              isHeroTop && "brightness-0 invert drop-shadow-[0_2px_12px_rgba(255,247,214,0.28)]",
            )}
          />
          <span
            className={cn(
              "hidden flex-col justify-center whitespace-nowrap text-[0.43rem] font-semibold leading-[1.12] min-[430px]:flex sm:text-[0.5rem] md:text-[0.62rem]",
              isHeroTop ? "text-[#fff7d6]/78" : "text-[#050505]/64",
            )}
          >
            <span>Motion Alignment × Joint Stability</span>
            <span>Physical Training</span>
          </span>
        </Link>

        <NavBar
          items={navItems}
          activeName={activeItem?.label}
          ariaLabel="주요 페이지"
          tone={isHeroTop ? "dark" : "light"}
          className="static bottom-auto left-auto z-auto mb-0 translate-x-0 sm:top-auto sm:pt-0"
        />
      </div>
    </header>
  );
}
